import { useEffect, useState, type JSX } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { PatientTodoDocumentDto } from '@ctms/contracts';
import { ClipboardList, Eye, Loader2, Printer, X } from 'lucide-react';

import { getApiErrorMessage } from '../../../api/client.js';
import { documentsApi } from '../documents.api.js';
import { downloadBlob } from '../vcl-print/vcl-print.js';

export interface PatientTodoPrintTarget {
  visitId: number;
  visitLabel: string;
  /** Wireframe order: „nazwisko i imię" (the card goes to the patient). */
  patientName: string;
}

export interface PatientTodoPrintDialogProps {
  target: PatientTodoPrintTarget;
  onClose: () => void;
}

/** Today's date for the subtitle — the print date, not a document field. */
function todayLabel(): string {
  return new Date().toLocaleDateString('pl-PL');
}

/**
 * Read-only preview of the composed card (REQ-DOC-017 data), mirroring the A4
 * layout of the worker template: header label:value pairs, the tasks table,
 * the upcoming-appointments table, the EMPTY home-care box (handwritten area,
 * Q-DOC-005 decision 2 — no system source) and the single staff-signature
 * place. Nullable fields print EMPTY — never a dash placeholder (B1 decision).
 * The header subset has NO PESEL in any shape (frozen feature).
 */
function PatientTodoPreview({ doc }: { doc: PatientTodoDocumentDto }): JSX.Element {
  const h = doc.header;
  return (
    <div className="space-y-4" data-testid="patient-todo-preview">
      <section className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Nagłówek A4
        </h3>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Pacjent:</dt>
            <dd className="font-medium text-slate-200">
              {h.lastName} {h.firstName}
            </dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Numer screeningowy:</dt>
            <dd className="font-mono text-cyan-300">{h.screeningNumber}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Badanie:</dt>
            <dd className="text-slate-300">
              {h.protocolNumber} · {h.siteNumber}
            </dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Wizyta:</dt>
            <dd className="text-slate-300">
              {h.visitName} · {h.visitDate}
            </dd>
          </div>
        </dl>
      </section>

      <section className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Twoje zadania do wykonania
        </h3>
        {doc.tasks.length > 0 ? (
          <table className="w-full text-left text-xs text-slate-200">
            <thead className="border-b border-slate-800 text-[11px] uppercase text-slate-400">
              <tr>
                <th className="px-2 py-1.5">Zadanie</th>
                <th className="px-2 py-1.5">Uwagi</th>
                <th className="px-2 py-1.5">Rozpoczęto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {doc.tasks.map((task) => (
                <tr key={task.taskId} className="text-slate-300">
                  <td className="px-2 py-1.5 font-medium text-slate-200">{task.name}</td>
                  <td className="px-2 py-1.5">{task.comments ?? ''}</td>
                  <td className="px-2 py-1.5 font-mono">{task.startDate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="px-2 text-xs text-slate-500">Brak otwartych zadań.</p>
        )}
      </section>

      <section className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Najbliższe terminy
        </h3>
        {doc.appointments.length > 0 ? (
          <table className="w-full text-left text-xs text-slate-200">
            <thead className="border-b border-slate-800 text-[11px] uppercase text-slate-400">
              <tr>
                <th className="px-2 py-1.5">Wizyta</th>
                <th className="px-2 py-1.5">Data</th>
                <th className="px-2 py-1.5">Godzina</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {doc.appointments.map((appointment, index) => (
                // The composed rows carry no ids (presentation order, VCL
                // convention) — the server order is stable, index is a safe key.
                <tr key={index} className="text-slate-300">
                  <td className="px-2 py-1.5 font-medium text-slate-200">
                    {appointment.visitName}
                  </td>
                  <td className="px-2 py-1.5 font-mono">{appointment.visitDate}</td>
                  <td className="px-2 py-1.5 font-mono">{appointment.visitTime ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="px-2 text-xs text-slate-500">Brak zaplanowanych wizyt.</p>
        )}
      </section>

      <section className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Zalecenia domowe
        </h3>
        {/* Handwritten area — no system source exists (Q-DOC-005 decision 2);
            the box is ALWAYS empty, on the preview and on the A4 alike. */}
        <div
          data-testid="patient-todo-home-care-box"
          aria-label="Puste miejsce na odręczne zalecenia domowe"
          className="min-h-28 rounded border border-dashed border-slate-600"
        />
      </section>

      <div className="flex justify-end">
        <div className="text-right text-xs text-slate-400">
          <div className="mb-1">Podpis personelu wydającego:</div>
          <div className="ml-auto h-px w-56 bg-slate-500" />
        </div>
      </div>
    </div>
  );
}

/**
 * Patient todo-list card print dialog — the Day Plan trigger of Domain 7
 * (issue #31 B2, REQ-DOC-017). ONE visit identifier drives both endpoints:
 * `generate` composes the JSON preview (frozen scenario: the composition fires
 * as soon as the dialog opens), `renderPdf` returns the physical A4 printout
 * (SLA < 1,5 s, prod p95 metric). Mirrors the VCL/B1 dialog convention; the
 * download button stays disabled until the preview is ready (accepted
 * wireframe, loading state).
 */
export function PatientTodoPrintDialog({
  target,
  onClose,
}: PatientTodoPrintDialogProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pdfNotice, setPdfNotice] = useState<string | null>(null);

  const generate = useMutation({
    mutationFn: (): Promise<PatientTodoDocumentDto> =>
      documentsApi.generatePatientTodo(target.visitId),
    onSuccess: () => {
      setErrorMessage(null);
    },
    onError: (error) => {
      void getApiErrorMessage(error).then(setErrorMessage);
    },
  });

  const renderPdf = useMutation({
    mutationFn: (): Promise<Blob> => documentsApi.renderPatientTodoPdf(target.visitId),
    onSuccess: (blob) => {
      downloadBlob(blob, `patient-todo-${target.visitId}.pdf`);
      setPdfNotice('Pobrano kartę zaleceń pacjenta');
    },
    onError: (error) => {
      void getApiErrorMessage(error).then(setErrorMessage);
    },
  });

  // The preview composes on open — no extra gesture (B1 precedent; mutation,
  // because generate appends to the generation log, REQ-DOC-020).
  useEffect(() => {
    generate.mutate();
  }, []);

  // Error state without a composed document: the retry gesture replaces the
  // preview label (accepted wireframe: „Spróbuj ponownie" → #todo-preview).
  const failedWithoutPreview = errorMessage !== null && generate.data === undefined;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Karta zaleceń pacjenta"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-xs"
    >
      <div className="flex max-h-[85vh] w-full max-w-[900px] flex-col rounded-xl border border-slate-800 bg-slate-900 text-slate-100 shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-cyan-950 text-cyan-400 ring-1 ring-cyan-800">
              <ClipboardList className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-100">Karta zaleceń pacjenta</h2>
              <div className="mt-1 flex items-center gap-4 text-xs text-slate-400">
                <span>
                  Wizyta: <strong className="font-mono text-cyan-300">{target.visitLabel}</strong>
                </span>
                <span>•</span>
                <span>
                  Pacjent: <strong className="text-slate-200">{target.patientName}</strong>
                </span>
                <span>•</span>
                <span>
                  Data wydruku: <strong className="text-slate-200">{todayLabel()}</strong>
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid="close-patient-todo-dialog"
            onClick={onClose}
            aria-label="Zamknij okno karty zaleceń"
            className="rounded-md border border-slate-700 bg-slate-850 p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          {failedWithoutPreview ? (
            <div
              role="alert"
              className="rounded-md border border-rose-700 bg-rose-950/80 px-4 py-3 text-xs text-rose-200"
            >
              <div className="mb-1 font-semibold">Nie udało się przygotować karty zaleceń</div>
              {errorMessage}
            </div>
          ) : (
            errorMessage !== null && (
              <div
                role="alert"
                className="mb-4 rounded-md border border-rose-700 bg-rose-950/80 px-4 py-2 text-xs text-rose-200"
              >
                {errorMessage}
              </div>
            )
          )}
          {generate.data !== undefined ? (
            <PatientTodoPreview doc={generate.data} />
          ) : generate.isIdle || generate.isPending ? (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Kompozycja dokumentu na serwerze…
            </div>
          ) : failedWithoutPreview ? null : (
            <p className="text-xs text-slate-500">
              Podgląd nie został wygenerowany — użyj przycisku „Podgląd” albo od razu pobierz
              wydruk A4 (PDF).
            </p>
          )}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-slate-800 px-6 py-4">
          <div aria-live="polite" className="text-xs text-emerald-300">
            {pdfNotice}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setErrorMessage(null);
                generate.mutate();
              }}
              disabled={generate.isPending}
              className="flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-200 transition-colors hover:bg-slate-800 disabled:opacity-50"
            >
              {generate.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Eye className="h-3.5 w-3.5" />
              )}
              {failedWithoutPreview ? 'Spróbuj ponownie' : 'Podgląd'}
            </button>
            <button
              type="button"
              onClick={() => {
                setErrorMessage(null);
                setPdfNotice(null);
                renderPdf.mutate();
              }}
              // Accepted wireframe (loading state): the download waits for a
              // ready preview — the composition result comes first.
              disabled={renderPdf.isPending || generate.data === undefined}
              className="flex items-center gap-1.5 rounded-md border border-cyan-700 bg-cyan-950/60 px-3 py-1.5 text-xs font-medium text-cyan-200 transition-colors hover:bg-cyan-900/60 disabled:opacity-50"
            >
              {renderPdf.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Printer className="h-3.5 w-3.5" />
              )}
              Pobierz PDF
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-300 transition-colors hover:bg-slate-800"
            >
              Zamknij
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
